import { getPointsExperiences } from 'actions/pointsExperiences'
import { getPointsProducts } from 'actions/pointsProducts'
import Spinner from 'components/Spinner'
import TabsContainer from 'components/Tabs/Container'
import useDispatch from 'hooks/useDispatch'
import useFlag from 'hooks/useFlag'
import useFlagsReady from 'hooks/useFlagsReady'
import { buildTranslate } from 'locales'
import React, { useEffect, useMemo, useState } from 'react'
import { Container, Row } from 'react-bootstrap-five'
import Helmet from 'react-helmet'
import { history } from 'store'
import Activity from './Activity'
import Configuration, { Tabs as ConfigurationTabs } from './Configuration'
import EffectiveDiscountRate from './EffectiveDiscountRate'
import ExperienceBadge from './ExperienceBadge'
import Marketplace from './Marketplace'
import PointActivityByLifecycleStageTab from './PointActivityByLifecycleStageTab'

const t = buildTranslate('points')

enum Tabs {
  ACTIVITY = 'activity',
  POINT_ACTIVITY_BY_LIFECYCLE_STAGE = 'point_activity_by_lifecycle_stage',
  MARKETPLACE = 'marketplace',
  CONFIGURATION = 'configuration',
  EFFECTIVE_DISCOUNT_RATE = 'effective_discount_rate',
}

// The tab ids this page actually renders. The lifecycle-stage tab is spliced
// into the array only when the flag is on, so anything deriving the active tab
// has to clamp to this or it can point at a tab with no pane and no nav link.
const visibleTabIds = (
  isPointActivityByLifecycleStageEnabled: boolean
): string[] => [
  Tabs.ACTIVITY,
  ...(isPointActivityByLifecycleStageEnabled
    ? [Tabs.POINT_ACTIVITY_BY_LIFECYCLE_STAGE]
    : []),
  Tabs.MARKETPLACE,
  Tabs.EFFECTIVE_DISCOUNT_RATE,
  Tabs.CONFIGURATION,
]

// Which tab the current URL hash selects. Takes the flag explicitly so it is
// evaluated during render with the settled value, not captured once at mount.
const resolveTab = (
  isPointActivityByLifecycleStageEnabled: boolean
): Tabs | string => {
  const param = history.location.hash.replace('#', '')
  if (
    param === Tabs.POINT_ACTIVITY_BY_LIFECYCLE_STAGE &&
    !isPointActivityByLifecycleStageEnabled
  ) {
    return Tabs.ACTIVITY
  }
  if (Object.values(Tabs).includes(param as Tabs)) {
    return param as Tabs
  }
  const isActiveTab = tabs => Object.values(tabs).some(t => t === param)
  if (isActiveTab(ConfigurationTabs)) return Tabs.CONFIGURATION

  return Tabs.ACTIVITY
}

const Landing: React.FC = () => {
  const isPointActivityByLifecycleStageEnabled = useFlag(
    'mx-promote-insights-lab-reports',
    false
  )
  const areFlagsReady = useFlagsReady()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const dispatch = useDispatch()
  // Only the user's explicit pick lives in state. Which tab renders is derived
  // below, during render.
  const [selectedTab, setSelectedTab] = useState<Tabs | string | null>(null)

  // Derived, not corrected in an effect — that was the earlier attempt here
  // and it cannot work. `useFlag` reads false both when the flag is off and
  // while LaunchDarkly is still evaluating this merchant's context, so the tab
  // must be resolved with the settled value. But a parent's useEffect runs
  // AFTER its children mount, and TabsContainer copies `defaultTab` into its
  // own state at mount and never re-reads it — so on the render where
  // readiness flipped, the container had already snapshotted the stale value
  // and a `setActiveTab` in an effect had nowhere to land. Resolving during
  // render puts the right value in the same commit the container mounts.
  //
  // Clamped to the tabs actually rendered. On its own the clamp only fixes
  // the first mount: TabsContainer copies `defaultTab` into its own state at
  // mount and never re-reads it, so recomputing `activeTab` here cannot move
  // an already-mounted container. That matters for the kill-switch case — the
  // flag being switched off mid-session while the user sits on the lifecycle
  // tab, where its pane and nav link disappear while the container's key
  // still points at it, leaving the tab bar with nothing under it. Keying the
  // container on the visible set is what makes the clamp effective there: the
  // key changes only when a tab appears or disappears, remounting the
  // container so it picks up the corrected `activeTab`.
  const visible = visibleTabIds(isPointActivityByLifecycleStageEnabled)
  const activeTab =
    selectedTab && visible.includes(selectedTab)
      ? selectedTab
      : resolveTab(isPointActivityByLifecycleStageEnabled)

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true)
      await Promise.all([
        dispatch(getPointsExperiences()),
        dispatch(getPointsProducts()),
      ])
      setIsLoading(false)
    }

    fetchData()
  }, [dispatch])

  const getTitle = useMemo(() => {
    switch (activeTab) {
      case Tabs.ACTIVITY:
        return t('activity.page_title')
      case Tabs.POINT_ACTIVITY_BY_LIFECYCLE_STAGE:
        return t('point_activity_by_lifecycle_stage.page_title')
      case Tabs.MARKETPLACE:
        return t('marketplace.page_title')
      case Tabs.CONFIGURATION:
        return t('configuration.page_title')
      case Tabs.EFFECTIVE_DISCOUNT_RATE:
        return t('effective_discount_rate.page_title')
    }
  }, [activeTab])

  return (
    <Container fluid className="p-0 white-bkg">
      <Helmet>
        <title>{getTitle}</title>
      </Helmet>
      <Container>
        <Row className="mt-xl mb-xs">
          <Spinner isLoading={isLoading} size="1x">
            <ExperienceBadge />
          </Spinner>
        </Row>
        <Row className="mb-l">
          <h2 className="mt-0 mb-0 mr-2 inline-block">{t('landing.title')}</h2>
        </Row>
      </Container>
      {/* `!areFlagsReady` is part of the loading condition because
          TabsContainer copies `defaultTab` into its own useState at mount and
          never re-reads it — so it must not mount until the flag (and hence
          the resolved tab) has settled, or the corrected value above would
          have nowhere to land. */}
      <Spinner
        isLoading={isLoading || !areFlagsReady}
        size="4x"
        className="text-center margin-top-huge"
      >
        <TabsContainer
          key={visible.join('|')}
          defaultTab={activeTab}
          shouldReplaceHash
          onSelect={tab => setSelectedTab(tab)}
          tabs={[
            {
              id: Tabs.ACTIVITY,
              name: t('activity.title'),
              content: <Activity key={Tabs.ACTIVITY} />,
            },
            ...(isPointActivityByLifecycleStageEnabled
              ? [
                  {
                    id: Tabs.POINT_ACTIVITY_BY_LIFECYCLE_STAGE,
                    name: t('point_activity_by_lifecycle_stage.title'),
                    content: (
                      <PointActivityByLifecycleStageTab
                        key={Tabs.POINT_ACTIVITY_BY_LIFECYCLE_STAGE}
                      />
                    ),
                  },
                ]
              : []),
            {
              id: Tabs.MARKETPLACE,
              name: t('marketplace.title'),
              content: <Marketplace key={Tabs.MARKETPLACE} />,
            },
            {
              id: Tabs.EFFECTIVE_DISCOUNT_RATE,
              name: t('effective_discount_rate.title'),
              content: (
                <EffectiveDiscountRate key={Tabs.EFFECTIVE_DISCOUNT_RATE} />
              ),
            },
            {
              id: Tabs.CONFIGURATION,
              name: t('configuration.title'),
              content: <Configuration key={Tabs.CONFIGURATION} />,
            },
          ]}
        />
      </Spinner>
    </Container>
  )
}

export default Landing
